import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  LinearProgress,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  Code as CodeIcon,
  Archive as ArchiveIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  CreateNewFolder as NewFolderIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import FileManagementService, { ProjectFile } from '../../services/FileManagementService';
import ProjectService, { Project } from '../../services/ProjectService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`files-tabpanel-${index}`}
      aria-labelledby={`files-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * Files Management Component for Project Manager
 * Handles file uploads, organization, sharing, and project document management
 */
const FilesManagement: React.FC = () => {
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProject, setFilterProject] = useState<number | null>(null);
  const [filterFileType, setFilterFileType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'size' | 'created_at' | 'download_count'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFile, setSelectedFile] = useState<ProjectFile | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [currentParentId, setCurrentParentId] = useState<number | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ id: number | null; name: string }>>([
    { id: null, name: 'Root' }
  ]);
  const [currentFolder, setCurrentFolder] = useState<ProjectFile | null>(null);
  const [projects, setProjects] = useState<Array<{ id: number; name: string }>>([]);
  const [currentProjectId, setCurrentProjectId] = useState<number | null>(null);

  useEffect(() => {
    loadProjects();
    loadFiles();
  }, [activeTab, filterProject, filterFileType, sortBy, sortOrder, searchTerm, currentParentId]);

  const loadProjects = async () => {
    try {
      // Load user's managed projects
      const response = await fetch('/api/v1/project-manager/projects', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Accept': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success && data.data) {
        setProjects(data.data);
      }
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  };

  const loadFiles = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Build filters based on active tab
      const filters: any = {
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      if (searchTerm) {
        filters.search = searchTerm;
      }

      if (filterProject) {
        filters.project_id = filterProject;
      }

      if (filterFileType && filterFileType !== 'all') {
        filters.file_type = filterFileType;
      }

      // Apply tab-specific filters
      switch (activeTab) {
        case 1: // Recent
          filters.recent = true;
          break;
        case 2: // Shared
          filters.shared = true;
          break;
        case 3: // My Files
          filters.my_files = true;
          break;
        default:
          // Only filter by parent_id in "All Files" tab
          // This ensures proper folder navigation
          filters.parent_id = currentParentId;
          break;
      }

      const data = await FileManagementService.getFiles(filters);
      setFiles(data);
    } catch (err) {
      setError('Failed to load files. Please try again.');
      console.error('Error loading files:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, file: ProjectFile) => {
    setAnchorEl(event.currentTarget);
    setSelectedFile(file);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFile(null);
  };

  const handleFileUpload = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    
    // Check if we need to select a project (at root with no project selected)
    if (!currentProjectId && currentParentId === null && !selectedProjectId) {
      setError('Please select a project before uploading files.');
      return;
    }
    
    try {
      setUploadProgress(0);
      setUploadDialogOpen(true);
      
      // Upload first file (can be extended to support multiple files)
      const file = fileList[0];
      
      // Use currentProjectId if in a folder, otherwise use selectedProjectId
      const projectId = currentProjectId || selectedProjectId;
      
      if (!projectId) {
        setError('Please select a project before uploading files.');
        setUploadDialogOpen(false);
        return;
      }
      
      await FileManagementService.uploadFile(
        {
          project_id: projectId!,
          file: file,
          parent_id: currentParentId,
        },
        (progressEvent: any) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      );
      
      setUploadDialogOpen(false);
      loadFiles(); // Reload files after upload
    } catch (err) {
      setError('Failed to upload file. Please try again.');
      console.error('Error uploading file:', err);
      setUploadDialogOpen(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    // Check if we need to select a project (at root with no project selected)
    if (!currentProjectId && currentParentId === null && !selectedProjectId) {
      setError('Please select a project before creating folders.');
      return;
    }
    
    try {
      // Use currentProjectId if in a folder, otherwise use selectedProjectId
      const projectId = currentProjectId || selectedProjectId;
      
      if (!projectId) {
        setError('Please select a project before creating folders.');
        return;
      }
      
      await FileManagementService.createFolder({
        project_id: projectId!,
        name: newFolderName,
        parent_id: currentParentId,
      });
      
      setFolderDialogOpen(false);
      setNewFolderName('');
      loadFiles();
    } catch (err) {
      setError('Failed to create folder. Please try again.');
      console.error('Error creating folder:', err);
    }
  };

  const handleDownload = async (file: ProjectFile) => {
    try {
      const downloadName = file.type === 'folder' ? `${file.name}.zip` : file.name;
      await FileManagementService.triggerDownload(file.id, downloadName);
      
      // Reload to update download count (for files)
      if (file.type === 'file') {
        loadFiles();
      }
    } catch (err) {
      setError(`Failed to download ${file.type === 'folder' ? 'folder' : 'file'}. Please try again.`);
      console.error('Error downloading:', err);
    }
  };

  const handleDelete = async (file: ProjectFile) => {
    if (!window.confirm(`Are you sure you want to delete "${file.name}"?`)) {
      return;
    }
    
    try {
      await FileManagementService.deleteFile(file.id);
      loadFiles();
      handleMenuClose();
    } catch (err) {
      setError('Failed to delete file. Please try again.');
      console.error('Error deleting file:', err);
    }
  };

  const handleFolderClick = (folder: ProjectFile) => {
    if (folder.type !== 'folder') return;
    
    setCurrentParentId(folder.id);
    setCurrentFolder(folder);
    setCurrentProjectId(folder.project_id); // Track the project of current folder
    
    // Add to breadcrumbs if not already there
    const existingIndex = breadcrumbs.findIndex(b => b.id === folder.id);
    if (existingIndex === -1) {
      setBreadcrumbs([...breadcrumbs, { id: folder.id, name: folder.name }]);
    } else {
      // Navigate back to this folder - trim breadcrumbs
      setBreadcrumbs(breadcrumbs.slice(0, existingIndex + 1));
    }
  };

  const handleBreadcrumbClick = (breadcrumb: { id: number | null; name: string }, index: number) => {
    setCurrentParentId(breadcrumb.id);
    setCurrentFolder(null);
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
    
    // Reset project if going back to root
    if (breadcrumb.id === null) {
      setCurrentProjectId(null);
    }
  };

  const handleBackClick = () => {
    if (breadcrumbs.length > 1) {
      const newBreadcrumbs = breadcrumbs.slice(0, -1);
      const parentBreadcrumb = newBreadcrumbs[newBreadcrumbs.length - 1];
      setCurrentParentId(parentBreadcrumb.id);
      setCurrentFolder(null);
      setBreadcrumbs(newBreadcrumbs);
      
      // Reset project if going back to root
      if (parentBreadcrumb.id === null) {
        setCurrentProjectId(null);
      }
    }
  };

  const getFileIcon = (file: ProjectFile) => {
    if (file.type === 'folder') return <FolderIcon color="primary" />;
    
    switch (file.file_type) {
      case 'image': return <ImageIcon color="success" />;
      case 'pdf': return <PdfIcon color="error" />;
      case 'document': return <DocIcon color="primary" />;
      case 'code': return <CodeIcon color="warning" />;
      case 'archive': return <ArchiveIcon color="secondary" />;
      default: return <FileIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const tabs = [
    { label: 'All Files', filter: 'all' },
    { label: 'Recent', filter: 'recent' },
    { label: 'Shared', filter: 'shared' },
    { label: 'My Files', filter: 'my' },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Files & Documents
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<NewFolderIcon />}
            onClick={() => setFolderDialogOpen(true)}
          >
            New Folder
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            component="label"
          >
            Upload Files
            <input
              type="file"
              hidden
              multiple
              onChange={(e) => handleFileUpload(e.target.files)}
            />
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={loadFiles}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="files tabs">
            {tabs.map((tab, index) => (
              <Tab key={tab.filter} label={tab.label} />
            ))}
          </Tabs>
        </Box>

        <CardContent>
          {/* Breadcrumbs Navigation */}
          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
            {breadcrumbs.length > 1 && (
              <IconButton size="small" onClick={handleBackClick}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                return isLast ? (
                  <Box key={crumb.id || 'root'} display="flex" alignItems="center" gap={0.5}>
                    {index === 0 ? <HomeIcon fontSize="small" /> : <FolderOpenIcon fontSize="small" />}
                    <Typography color="text.primary" fontWeight="medium">
                      {crumb.name}
                    </Typography>
                  </Box>
                ) : (
                  <Link
                    key={crumb.id || 'root'}
                    component="button"
                    variant="body1"
                    onClick={() => handleBreadcrumbClick(crumb, index)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                    underline="hover"
                  >
                    {index === 0 ? <HomeIcon fontSize="small" /> : <FolderIcon fontSize="small" />}
                    {crumb.name}
                  </Link>
                );
              })}
            </Breadcrumbs>
          </Box>

          {/* Filters and Search */}
          <Box display="flex" gap={2} alignItems="center" sx={{ mb: 3 }} flexWrap="wrap">
            <TextField
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>File Type</InputLabel>
              <Select
                value={filterFileType}
                label="File Type"
                onChange={(e) => setFilterFileType(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="image">Images</MenuItem>
                <MenuItem value="pdf">PDFs</MenuItem>
                <MenuItem value="document">Documents</MenuItem>
                <MenuItem value="code">Code</MenuItem>
                <MenuItem value="archive">Archives</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value as any)}
              >
                <MenuItem value="created_at">Date</MenuItem>
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="size">Size</MenuItem>
                <MenuItem value="download_count">Downloads</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Order</InputLabel>
              <Select
                value={sortOrder}
                label="Order"
                onChange={(e) => setSortOrder(e.target.value as any)}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* File List */}
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Uploaded By</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Downloads</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file) => (
                  <TableRow 
                    key={file.id} 
                    hover 
                    onClick={() => file.type === 'folder' && handleFolderClick(file)}
                    sx={{ cursor: file.type === 'folder' ? 'pointer' : 'default' }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getFileIcon(file)}
                        <Box>
                          <Typography 
                            variant="body2" 
                            fontWeight="medium"
                            sx={{ 
                              color: file.type === 'folder' ? 'primary.main' : 'inherit',
                              textDecoration: file.type === 'folder' ? 'underline' : 'none'
                            }}
                          >
                            {file.name}
                          </Typography>
                          {file.tags && file.tags.length > 0 && (
                            <Box display="flex" gap={0.5} mt={0.5}>
                              {file.tags.slice(0, 2).map((tag: string) => (
                                <Chip key={tag} label={tag} size="small" variant="outlined" />
                              ))}
                              {file.tags.length > 2 && (
                                <Chip label={`+${file.tags.length - 2}`} size="small" variant="outlined" />
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {file.type === 'folder' ? '-' : file.formatted_size}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                          {file.uploader?.name?.split(' ').map((n: string) => n[0]).join('') || 'U'}
                        </Avatar>
                        {file.uploader?.name || 'Unknown'}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(file.created_at)}</TableCell>
                    <TableCell>
                      <Chip label={`Project ${file.project_id}`} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {file.download_count}
                        {file.is_shared && <ShareIcon fontSize="small" color="primary" />}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, file)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {files.length === 0 && !loading && (
            <Box textAlign="center" py={4}>
              <FileIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Files Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? 'No files match your search criteria.' : 'Upload files to get started.'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* File Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedFile) handleDownload(selectedFile);
          handleMenuClose();
        }}>
          <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
          {selectedFile?.type === 'folder' ? 'Download as ZIP' : 'Download'}
        </MenuItem>
        {selectedFile?.type === 'folder' && (
          <MenuItem onClick={() => {
            if (selectedFile) handleFolderClick(selectedFile);
            handleMenuClose();
          }}>
            <FolderOpenIcon sx={{ mr: 1 }} fontSize="small" />
            Open Folder
          </MenuItem>
        )}
        <MenuItem onClick={handleMenuClose}>
          <ShareIcon sx={{ mr: 1 }} fontSize="small" />
          Share
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Rename
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          if (selectedFile) handleDelete(selectedFile);
        }} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Upload Progress Dialog */}
      <Dialog open={uploadDialogOpen} disableEscapeKeyDown>
        <DialogTitle>Uploading Files</DialogTitle>
        <DialogContent>
          {!currentProjectId && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="upload-project-select-label">Select Project *</InputLabel>
              <Select
                labelId="upload-project-select-label"
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(Number(e.target.value))}
                label="Select Project *"
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box sx={{ width: 300, mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Uploading files... {uploadProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        </DialogContent>
      </Dialog>

      {/* New Folder Dialog */}
      <Dialog open={folderDialogOpen} onClose={() => setFolderDialogOpen(false)}>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          {!currentProjectId && (
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel id="folder-project-select-label">Select Project *</InputLabel>
              <Select
                labelId="folder-project-select-label"
                value={selectedProjectId || ''}
                onChange={(e) => setSelectedProjectId(Number(e.target.value))}
                label="Select Project *"
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setFolderDialogOpen(false);
            setNewFolderName('');
          }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateFolder}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FilesManagement;